import type { Submission, VerificationBadge, VerifiedCompletion } from '@engineer-os/engine';

export interface AllowanceWindow { used: number; limit: number; resetAt: number | null }
export interface VerificationAllowance {
  hour: AllowanceWindow; day: AllowanceWindow; month: AllowanceWindow;
  /** The deployment itself is closed to new checks; nothing the learner spent. */
  deploymentPaused: boolean;
  nextEligibleAt: number | null;
}
export interface VerificationStatus {
  enabled: boolean; signedIn: boolean; completions: VerifiedCompletion[];
  /**
   * The worlds those completions add up to, as the Worker computed them.
   *
   * Sent so a learner's own screen and the public page can never disagree
   * about what has been earned. The public page derives its own from the rows
   * it validated (lib/person.ts) because it is read by strangers; this one is
   * the account's own view of its own record, and the server is the authority
   * on it.
   */
  badges?: VerificationBadge[];
  pending?: { id: string; missionId: string; state: 'queued' | 'running' }[];
  limits?: VerificationAllowance;
}
export interface VerificationResult {
  state: 'issued' | 'queued' | 'running' | 'passed' | 'failed' | 'error';
  attemptId?: string; completion?: VerifiedCompletion; failed?: string[];
  /** Visible objectives that never ran because one they depend on failed (lib/checkpoint.ts). */
  skipped?: string[];
  feedback?: Record<string, { en: string; ar?: string }>;
  /** The server answered from a record it already had, without grading again. */
  reused?: boolean;
  /** An already-pending snapshot, possibly submitted by another tab. */
  resumed?: boolean;
  limits?: VerificationAllowance;
}
interface Reservation { id?: string; expiresAt?: number; resumed?: boolean; limits?: VerificationAllowance }
export const VERIFICATION_REQUEST_TIMEOUT = 20_000;

/**
 * `body` is the exact string sent, never an object: the server's receipt
 * lookup and its conflict check both hash these bytes, so the bytes that get
 * hashed here have to be the bytes that get sent.
 */
async function call<T>(path: string, body?: string, signal?: AbortSignal): Promise<T> {
  const request = new AbortController();
  const abort = () => request.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) request.abort();
  const timer = setTimeout(() => request.abort(), VERIFICATION_REQUEST_TIMEOUT);
  try {
    const response = await fetch(`/api/verification${path}`, {
      method: body === undefined ? 'GET' : 'POST', credentials: 'same-origin', cache: 'no-store', signal: request.signal,
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' }, body,
    });
    const value = await response.json();
    if (!response.ok) throw Object.assign(new Error('Verification request failed'), { status: response.status, body: value });
    return value as T;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

export const verificationStatus = (signal?: AbortSignal) => call<VerificationStatus>('', undefined, signal);

/**
 * This learner's starting repositories, one per salted level.
 *
 * The guest needs the answer before it extracts anything, so this is fetched
 * on the way into such a level rather than kept up to date in the background.
 * A 401 means signed out, which is not an error here: a checkpoint asks for an
 * account anyway, and the caller turns the empty answer into the right refusal.
 */
export async function seedSalts(signal?: AbortSignal): Promise<Record<string, string>> {
  const value = await call<{ seeds?: Record<string, string> }>('/seeds', undefined, signal);
  return value.seeds ?? {};
}

async function digestOf(text: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * A transport retry uses the same attempt and the same bytes; it must not buy
 * another grade. Neither must re-submitting work the server has already judged,
 * which is what the digest sent with the reservation is for: unchanged work
 * comes back as the verdict it already got, and no attempt is spent.
 */
export async function verifySubmission(submission: Submission, signal: AbortSignal): Promise<VerificationResult> {
  const result = await submitVerification(submission, signal);
  return result.attemptId ? waitForVerification(result.attemptId, signal, result) : result;
}

/** Admit/upload once, without tying the local Check button to remote polling.
 * Remember the reservation before uploading so a timeout can retry the same
 * attempt and exact snapshot, even if the upload response was lost.
 */
export async function submitVerification(submission: Submission, signal: AbortSignal,
  options: { attemptId?: string; onReserved?: (id: string) => void } = {}): Promise<VerificationResult> {
  const text = JSON.stringify(submission);
  const opened: Reservation & VerificationResult = options.attemptId ? { id: options.attemptId, resumed: true, state: 'issued' } : await call<Reservation & VerificationResult>('/attempts',
    JSON.stringify({ missionId: submission.missionId, digest: await digestOf(text) }), signal);
  // No id means the server answered from its own records: a completion receipt
  // or this account's earlier verdict on these exact bytes.
  if (!opened.id) return opened;
  const attemptId = opened.id;
  options.onReserved?.(attemptId);
  if (opened.resumed) {
    const existing = await verificationAttempt(attemptId, signal);
    if (existing.state !== 'issued') return { ...existing, attemptId, resumed: !options.attemptId };
  }
  let result: VerificationResult;
  try {
    result = await call<VerificationResult>(`/attempts/${attemptId}/submit`, text, signal);
  } catch (error) {
    if (signal.aborted || (error as { status?: number }).status) throw error;
    result = await call<VerificationResult>(`/attempts/${attemptId}/submit`, text, signal);
  }
  return { ...result, attemptId, limits: result.limits ?? opened.limits };
}

export const verificationAttempt = (id: string, signal?: AbortSignal) =>
  call<VerificationResult>(`/attempts/${id}`, undefined, signal);

/** A polling deadline stops waiting in this tab; the durable job keeps its identity. */
export async function waitForVerification(attemptId: string, signal: AbortSignal,
  initial?: VerificationResult, waitMs = 125_000): Promise<VerificationResult> {
  let result = initial ?? await call<VerificationResult>(`/attempts/${attemptId}`, undefined, signal);
  const deadline = Date.now() + waitMs;
  let delay = 2000;
  while ((result.state === 'running' || result.state === 'queued') && Date.now() < deadline) {
    await new Promise<void>((resolve, reject) => {
      const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
      const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, Math.min(delay, deadline - Date.now()));
      if (signal.aborted) abort(); else signal.addEventListener('abort', abort, { once: true });
    });
    try { result = await call<VerificationResult>(`/attempts/${attemptId}`, undefined, signal); }
    catch (error) {
      if (signal.aborted || ((error as { status?: number }).status ?? 500) < 500) throw error;
      // Transient status failures do not turn an accepted job into a failed grade.
    }
    delay = Math.min(10_000, delay * 1.5);
  }
  return { ...result, attemptId };
}
